import {RefObject, useEffect} from 'react';
import {isEditableTarget} from './isEditableTarget';

interface UseGridKeyboardOptions {
    rowSelector: string;
    cellSelector?: string;
    enabled?: boolean;
}

function isVisible(element: HTMLElement): boolean {
    return element.getClientRects().length > 0;
}

function getFocusable(cell: HTMLElement): HTMLElement | null {
    return cell.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
}

function focusCell(cell: HTMLElement, preferRow = false) {
    const focusable = preferRow ? null : getFocusable(cell);
    const target = focusable ?? cell;

    if (!focusable) {
        cell.tabIndex = -1;
    }

    target.focus();
    target.scrollIntoView({block: 'nearest', inline: 'nearest'});
}

export function useGridKeyboard(
    rootRef: RefObject<HTMLElement | null>,
    {rowSelector, cellSelector, enabled = true}: UseGridKeyboardOptions
) {
    useEffect(() => {
        const root = rootRef.current;

        if (!root || !enabled) {
            return;
        }

        const getRows = () =>
            Array.from(root.querySelectorAll<HTMLElement>(rowSelector)).filter(isVisible);

        const getCells = (row: HTMLElement) => {
            if (!cellSelector) {
                return [row];
            }

            const cells = Array.from(row.querySelectorAll<HTMLElement>(cellSelector)).filter(
                isVisible
            );

            return cells.length ? cells : [row];
        };

        const locate = (target: HTMLElement) => {
            const rows = getRows();
            const row = rows.find((item) => item.contains(target) || item === target);

            if (!row) {
                return null;
            }

            const cells = getCells(row);
            const cell = cells.find((item) => item === target || item.contains(target)) ?? cells[0];
            const rowIndex = rows.indexOf(row);
            const colIndex = Math.max(cells.indexOf(cell), 0);

            return {rows, row, cells, cell, rowIndex, colIndex};
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }

            if (isEditableTarget(event.target)) {
                return;
            }

            const target = event.target;

            if (!(target instanceof HTMLElement) || !root.contains(target)) {
                return;
            }

            if (target === root) {
                if (
                    event.key === 'ArrowDown' ||
                    event.key === 'ArrowRight' ||
                    event.key === 'Enter' ||
                    event.key === ' '
                ) {
                    const rows = getRows();
                    const firstRow = rows[0];

                    if (!firstRow) {
                        return;
                    }

                    const cells = getCells(firstRow);
                    const cell = cells[0];

                    if (!cell) {
                        return;
                    }

                    event.preventDefault();
                    focusCell(cell, !cellSelector);
                }

                return;
            }

            const location = locate(target);

            if (!location) {
                return;
            }

            const {rows, cells, rowIndex, colIndex} = location;

            const move = (nextRow: number, nextCol: number) => {
                const row = rows[nextRow];

                if (!row) {
                    return;
                }

                const rowCells = getCells(row);
                const cell = rowCells[Math.min(Math.max(nextCol, 0), rowCells.length - 1)];

                if (!cell) {
                    return;
                }

                event.preventDefault();
                focusCell(cell, !cellSelector);
            };

            switch (event.key) {
                case 'ArrowDown':
                    move(Math.min(rowIndex + 1, rows.length - 1), colIndex);
                    break;
                case 'ArrowUp':
                    move(Math.max(rowIndex - 1, 0), colIndex);
                    break;
                case 'ArrowRight':
                    if (cellSelector && colIndex < cells.length - 1) {
                        move(rowIndex, colIndex + 1);
                    } else {
                        move(Math.min(rowIndex + 1, rows.length - 1), 0);
                    }
                    break;
                case 'ArrowLeft':
                    if (cellSelector && colIndex > 0) {
                        move(rowIndex, colIndex - 1);
                    } else {
                        move(Math.max(rowIndex - 1, 0), cellSelector ? 999 : 0);
                    }
                    break;
                case 'PageUp':
                    move(0, colIndex);
                    break;
                case 'PageDown':
                    move(rows.length - 1, colIndex);
                    break;
                case 'Home':
                    move(rowIndex, 0);
                    break;
                case 'End':
                    move(rowIndex, cells.length - 1);
                    break;
                case 'Enter': {
                    if (!cellSelector) {
                        if (
                            target !== location.cell &&
                            target.closest('button, a, input, textarea, [role="button"]')
                        ) {
                            return;
                        }

                        event.preventDefault();
                        location.cell.click();
                        break;
                    }

                    const cell = location.cell;
                    const interactive = getFocusable(cell);

                    if (interactive && interactive !== target) {
                        event.preventDefault();
                        interactive.click();
                    } else if (!interactive) {
                        event.preventDefault();
                        cell.click();
                    }
                    break;
                }
                default:
                    break;
            }
        };

        root.addEventListener('keydown', onKeyDown);

        return () => {
            root.removeEventListener('keydown', onKeyDown);
        };
    }, [rootRef, rowSelector, cellSelector, enabled]);
}
